import { createFileRoute } from "@tanstack/react-router";
import { physicianSchema, safeJsonLd } from "@/lib/structured-data-core";
import { lazy, Suspense, useState } from "react";
import { X, User, GraduationCap, Award } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { SocialPosts } from "@/components/site/SocialPosts";
import hero from "@/assets/restore/lovitt-hero.png.asset.json";
import signature from "@/assets/restore/lovitt-signature.png.asset.json";
import bioPhoto from "@/assets/restore/lovitt-bio.png.asset.json";
import g1 from "@/assets/restore/lovitt-g1.webp.asset.json";
import g2 from "@/assets/restore/lovitt-g2.webp.asset.json";
import g3 from "@/assets/restore/lovitt-g3.webp.asset.json";
import g4 from "@/assets/restore/lovitt-g4.webp.asset.json";
import g5 from "@/assets/restore/lovitt-g5.webp.asset.json";
import { primaryVideoObject } from "@/lib/video-seo";
import { absoluteUrl } from "@/lib/absolute-url";

const Results = lazy(() => import("@/components/site/Results").then(m => ({ default: m.Results })));
const Reviews = lazy(() => import("@/components/site/Reviews").then(m => ({ default: m.Reviews })));
const QuoteForm = lazy(() => import("@/components/site/QuoteForm").then(m => ({ default: m.QuoteForm })));

const VIDEO_URL =
  "https://d17ehmrb6zqtjv.cloudfront.net/Videos/Videos%20with%20Captions/Restore_DocBio_Lovitt2022_V1.mp4";

const GALLERY = [g1.url, g2.url, g3.url, g4.url, g5.url];

export const Route = createFileRoute("/doctors/dr-shelly-lovitt")({
  head: () => ({
    links: [{ rel: "canonical", href: "https://restorehair.com/doctors/dr-shelly-lovitt/" }],
    meta: [
      { title: "Dr. Shelly Lovitt — Hair Restoration Physician | RESTORE® Hair" },
      {
        name: "description",
        content:
          "Dr. Shelly Lovitt is a board-certified physician who leads RESTORE's Denver office. She has 20+ years of medical experience and 1,000+ hair restorations.",
      },
      { property: "og:title", content: "Dr. Shelly Lovitt — Hair Restoration Physician" },
      {
        property: "og:description",
        content:
          "Meet Dr. Shelly Lovitt, board-certified physician and RESTORE® elite hair surgeon leading our Denver office.",
      },
      { property: "og:image", content: absoluteUrl(hero.url) },
      { name: "twitter:image", content: absoluteUrl(hero.url) },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: safeJsonLd(primaryVideoObject("/doctors/dr-shelly-lovitt/")),
      },
      {
        type: "application/ld+json",
        children: safeJsonLd(
          physicianSchema({
            name: "Dr. Shelly Lovitt",
            jobTitle: "Hair Restoration Physician",
            description: "Dr. Shelly Lovitt is a board-certified physician who leads RESTORE's Denver office. She has 20+ years of medical experience and 1,000+ hair restorations.",
            image: hero.url,
            pageUrl: "https://restorehair.com/doctors/dr-shelly-lovitt/",
          }),
        ),
      },
    ],
  }),
  component: DrLovittPage,
});

function DrLovittPage() {
  const [tab, setTab] = useState<"bio" | "education" | "licenses">("bio");
  const [videoOpen, setVideoOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background font-sans">
      <Header />
      <main>
        {/* Hero */}
        <section className="overflow-hidden bg-background">
          <div className="mx-auto grid max-w-7xl grid-cols-1 gap-8 px-4 pt-10 lg:grid-cols-2 lg:items-end lg:gap-4">
            <div className="pb-10 lg:pb-16">
              <img
                src={signature.url}
                alt="Dr. Shelly Lovitt signature"
                className="mb-4 h-16 w-auto object-contain"
                loading="eager"
              />
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground">
                Dr. Shelly Lovitt
              </h1>
              <p className="mt-3 text-lg md:text-xl font-semibold text-primary">
                Hair Restoration Physician — Denver
              </p>
              <p className="mt-6 text-base md:text-lg leading-relaxed text-muted-foreground">
                Dr. Shelly Lovitt is a distinguished hair restoration expert leading our Denver
                office. A trusted name in the field known for her precision and dedication to
                achieving remarkable outcomes, she has an outstanding track record of over one
                thousand successful hair restoration procedures.
              </p>
              <p className="mt-4 text-base md:text-lg leading-relaxed text-muted-foreground">
                As a board-certified physician and esteemed member of the International Society
                of Hair Restoration Surgeons, she brings 20+ years of medical expertise and more
                than 10 years in hair restoration to her role as a RESTORE® elite hair surgeon.
              </p>
            </div>
            <div className="relative flex justify-center lg:justify-end lg:self-end">
              <img
                src={hero.url}
                alt="Dr. Shelly Lovitt — Hair Restoration Physician"
                className="block h-[480px] w-full max-w-xl object-cover object-top md:h-[540px] lg:h-[640px]"
                loading="eager"
              />
              <button
                type="button"
                onClick={() => setVideoOpen(true)}
                aria-label="Play Dr. Shelly Lovitt's video"
                title="Play Video"
                className="absolute bottom-10 left-1/2 -translate-x-1/2 transition hover:opacity-90"
              >
                <img
                  src="/img/r/wp-2022-04-group-167.webp"
                  alt="Play Dr. Shelly Lovitt's video"
                  className="h-24 w-24 md:h-28 md:w-28"
                  loading="lazy"
                />
              </button>
            </div>
          </div>
        </section>

        {videoOpen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/80 p-4"
            role="dialog"
            aria-modal="true"
            onClick={() => setVideoOpen(false)}
          >
            <div className="relative w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                onClick={() => setVideoOpen(false)}
                aria-label="Close video"
                className="absolute -top-10 right-0 text-background"
              >
                <X size={28} />
              </button>
              <video src={VIDEO_URL} controls autoPlay className="w-full" />
            </div>
          </div>
        )}

        {/* Bio / Education / Licenses tabs */}
        <section className="bg-muted/40 py-12 md:py-16">
          <div className="mx-auto w-full max-w-6xl px-4">
            <div className="grid max-w-4xl grid-cols-3 gap-1">
              {([
                ["bio", "Full Bio", User],
                ["education", "Education", GraduationCap],
                ["licenses", "Licenses", Award],
              ] as const).map(([id, label, Icon]) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setTab(id)}
                  className={`inline-flex items-center justify-center gap-2 px-4 py-4 text-base md:text-lg font-semibold transition ${
                    tab === id
                      ? "bg-primary text-primary-foreground"
                      : "bg-card text-foreground shadow-sm hover:bg-card/80"
                  }`}
                >
                  <Icon size={20} />
                  {label}
                </button>
              ))}
            </div>

            <div className="bg-card px-4 py-10 md:px-10 md:py-14 shadow-sm">
              {tab === "bio" && (
                <div className="mx-auto max-w-6xl">
                  <div>
                    <h2 className="text-2xl md:text-3xl font-bold text-foreground">
                      Dr. Shelly Lovitt's Bio
                    </h2>
                    <div className="mt-4 space-y-4 text-base leading-relaxed text-muted-foreground">
                      <p>
                        Dr. Lovitt has worked alongside RESTORE's medical director Dr. James
                        Harris for nearly ten years and recognizes firsthand the value of patient
                        education, meticulous surgical standards, and procedures, and RESTORE's
                        uncompromised commitment to patient results.
                      </p>
                      <p>
                        "The procedures we perform at RESTORE not only enable patients to regrow
                        their own hair, but they also have a profound impact on a patient's
                        self-image and confidence," says Dr. Lovitt. "At RESTORE, we tailor our
                        procedures specifically to the needs of each patient, ensuring that they
                        achieve the most natural-looking and transformative results."
                      </p>
                      <p>
                        After more than 1000 procedures, Dr. Lovitt's expertise is unquestionable,
                        she stands tall among the elite surgeons in our Denver office who draw
                        patients from across the U.S with their reputation for experience.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {tab === "education" && (
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground">
                    Dr. Shelly Lovitt — Education
                  </h2>
                  <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2">
                    <div className="border border-border p-6">
                      <p className="text-sm uppercase tracking-wide text-primary">Doctor of Medicine (M.D.) 1996</p>
                      <p className="mt-2 text-lg font-semibold text-foreground">
                        Tulane School of Medicine
                      </p>
                      <p className="mt-1 text-sm text-muted-foreground">New Orleans, LA</p>
                    </div>
                    <div className="border border-border p-6">
                      <p className="text-sm uppercase tracking-wide text-primary">Bachelor of Arts 1992</p>
                      <p className="mt-2 text-lg font-semibold text-foreground">Tulane University</p>
                      <p className="mt-1 text-sm text-muted-foreground">New Orleans, LA</p>
                    </div>
                    <div className="border border-border p-6 md:col-span-2">
                      <p className="text-sm uppercase tracking-wide text-primary">Post Graduate Training</p>
                      <p className="mt-2 text-lg font-semibold text-foreground">
                        Alton Ochsner Medical Foundation
                      </p>
                      <p className="mt-1 text-sm text-muted-foreground">New Orleans, LA · 1996 to 2000</p>
                    </div>
                  </div>
                </div>
              )}

              {tab === "licenses" && (
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground">
                    Dr. Shelly Lovitt — Licenses
                  </h2>
                  <ul className="mt-6 space-y-4 text-base leading-relaxed text-muted-foreground">
                    <li className="border border-border p-6">
                      Board Certified in Obstetrics &amp; Gynecology, 2004
                    </li>
                    <li className="border border-border p-6">
                      Associate Member of International Society of Hair Restoration Surgery, Board Eligible
                    </li>
                    <li className="border border-border p-6">
                      Colorado Medical License, Active
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Gallery */}
        <section className="bg-background py-12 md:py-16">
          <div className="mx-auto max-w-6xl px-4">
            <h2 className="text-center text-2xl md:text-3xl font-bold text-foreground">
              Learn More About <span className="text-primary">Dr. Lovitt</span>
            </h2>
            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {GALLERY.map((url, i) => (
                <img
                  key={url}
                  src={url}
                  alt={`Dr. Shelly Lovitt at work ${i + 1}`}
                  loading="lazy"
                  className="w-full object-cover"
                />
              ))}
            </div>
          </div>
        </section>
        <Suspense fallback={null}>
          <Results resultsHref="https://restorehair.com/results/" resultsButtonLabel="See More Results" />
          <Reviews />
        </Suspense>
        <SocialPosts />
        <Suspense fallback={null}>
          <QuoteForm />
        </Suspense>
      </main>
      <Footer />
    </div>
  );
}